/**
 * React Error Boundary integrated with Datadog RUM
 * Based on https://docs.datadoghq.com/integrations/rum-react/
 */

import { Warning } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { logError } from '@ptek/client-logger';
import { Component, ErrorInfo, ReactNode } from 'react';
import { errorBoundaryStyles as styles } from './styles/errorBoundaryStyles';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, resetError: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary that reports React errors to Datadog RUM
 * Integrates with your existing logger which sends to RUM
 */
export class DatadogErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logError('ErrorBoundary', 'React component error', error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError);
      }

      return (
        <Box sx={styles.container}>
          <Box sx={styles.content}>
            <Box sx={styles.warningRow}>
              <Warning color='warning' sx={styles.warningIcon} />
              <Typography variant='body2' sx={styles.errorText}>
                Something went wrong
              </Typography>
            </Box>

            <Button
              onClick={this.resetError}
              variant='contained'
              color='primary'
              size='small'
              sx={styles.retryButton}
            >
              Try Again
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}
